import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Filter, X, Printer, FileSpreadsheet, BarChart3, Building2, FolderKanban,
  UserCheck, ShieldCheck, TrendingUp, Clock, CheckCircle2, AlertTriangle,
  Loader2, Calendar, Package, ClipboardList
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===================================================================
// قائمة التبويبات
const TABS = [
  { id: 'analytics', label: 'لوحة تحليلية', icon: BarChart3, color: 'orange' },
  { id: 'list', label: 'قائمة الكشوف', icon: ClipboardList, color: 'gray' },
  { id: 'supplier', label: 'حسب المورد', icon: Building2, color: 'purple' },
  { id: 'project', label: 'حسب المشروع', icon: FolderKanban, color: 'blue' },
  { id: 'creator', label: 'حسب المدخل', icon: UserCheck, color: 'green' },
  { id: 'approver', label: 'حسب المشرف', icon: ShieldCheck, color: 'indigo' },
];

const STATUS_COLORS = { approved: '#16a34a', pending: '#eab308', rejected: '#dc2626', draft: '#94a3b8' };

// ===================================================================
// لوحة الفلترة (مشتركة بين كل التبويبات)
function FiltersPanel({ filters, setFilters, options, onClear, activeCount }) {
  const update = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  return (
    <div className="bg-gray-50 border rounded-xl p-3 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reports-filters-panel">
      {/* بحث برقم الاستلام */}
      <div className="lg:col-span-2 sm:col-span-2">
        <label className="block text-xs text-gray-600 mb-1">🔍 بحث برقم الاستلام</label>
        <input
          type="text"
          value={filters.receipt_number}
          onChange={(e) => update('receipt_number', e.target.value)}
          placeholder="مثال: 1234 أو جزء من الرقم..."
          data-testid="filter-receipt-number"
          className="w-full h-10 px-3 border-2 border-orange-300 focus:border-orange-500 rounded-lg bg-white outline-none font-medium"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">المورد</label>
        <select value={filters.supplier_name} onChange={(e) => update('supplier_name', e.target.value)} data-testid="filter-supplier" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل الموردين</option>
          {(options.suppliers || []).map((s, i) => <option key={i} value={s.name}>{s.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">المشروع</label>
        <select value={filters.project_number} onChange={(e) => update('project_number', e.target.value)} data-testid="filter-project" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل المشاريع</option>
          {(options.projects || []).map((p, i) => <option key={i} value={p.number}>{p.number} - {p.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">مدخل الكشف</label>
        <select value={filters.creator_id} onChange={(e) => update('creator_id', e.target.value)} data-testid="filter-creator" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل المدخلين</option>
          {(options.creators || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">المشرف (المعتمِد)</label>
        <select value={filters.approver_id} onChange={(e) => update('approver_id', e.target.value)} data-testid="filter-approver" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل المشرفين</option>
          {(options.approvers || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">الحالة</label>
        <select value={filters.status} onChange={(e) => update('status', e.target.value)} data-testid="filter-status" className="w-full h-10 px-2 border rounded-lg bg-white">
          <option value="">كل الحالات</option>
          <option value="draft">مسودة</option>
          <option value="pending">معلق</option>
          <option value="approved">معتمد</option>
          <option value="rejected">مرفوض</option>
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">تاريخ الإدخال من</label>
        <input type="date" value={filters.date_from} onChange={(e) => update('date_from', e.target.value)} data-testid="filter-date-from" className="w-full h-10 px-2 border rounded-lg bg-white" />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">تاريخ الإدخال إلى</label>
        <input type="date" value={filters.date_to} onChange={(e) => update('date_to', e.target.value)} data-testid="filter-date-to" className="w-full h-10 px-2 border rounded-lg bg-white" />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-1">تاريخ الاعتماد النهائي من</label>
        <input type="date" value={filters.approval_date_from} onChange={(e) => update('approval_date_from', e.target.value)} data-testid="filter-approval-date-from" className="w-full h-10 px-2 border rounded-lg bg-white" />
      </div>
      <div className="lg:col-span-4 sm:col-span-2 flex justify-between items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs text-gray-600 mb-1">تاريخ الاعتماد النهائي إلى</label>
          <input type="date" value={filters.approval_date_to} onChange={(e) => update('approval_date_to', e.target.value)} data-testid="filter-approval-date-to" className="w-full h-10 px-2 border rounded-lg bg-white" />
        </div>
        {activeCount > 0 && (
          <button onClick={onClear} data-testid="clear-filters" className="h-10 px-4 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1 font-medium whitespace-nowrap">
            <X className="w-4 h-4" /> مسح ({activeCount})
          </button>
        )}
      </div>
    </div>
  );
}

// ===================================================================
// بطاقة KPI
function Kpi({ label, value, icon: Icon, color = 'orange', suffix = '' }) {
  const colors = {
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  };
  return (
    <div className={`rounded-xl p-3 border-2 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-1">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <span className="text-xs opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}{suffix && <span className="text-xs mr-1 opacity-70">{suffix}</span>}</div>
    </div>
  );
}

// ===================================================================
// شرائح الفلاتر النشطة (للطباعة)
function ActiveFilterChips({ filters, options, className = '' }) {
  const chips = [];
  if (filters.receipt_number) chips.push({ k: 'رقم الاستلام', v: filters.receipt_number });
  if (filters.supplier_name) chips.push({ k: 'المورد', v: filters.supplier_name });
  if (filters.project_number) {
    const p = options.projects?.find((x) => x.number === filters.project_number);
    chips.push({ k: 'المشروع', v: p ? `${p.number} - ${p.name}` : filters.project_number });
  }
  if (filters.creator_id) {
    const c = options.creators?.find((x) => x.id === filters.creator_id);
    chips.push({ k: 'المدخل', v: c?.name || filters.creator_id });
  }
  if (filters.approver_id) {
    const a = options.approvers?.find((x) => x.id === filters.approver_id);
    chips.push({ k: 'المشرف', v: a?.name || filters.approver_id });
  }
  if (filters.status) {
    const map = { draft: 'مسودة', pending: 'معلق', approved: 'معتمد', rejected: 'مرفوض' };
    chips.push({ k: 'الحالة', v: map[filters.status] || filters.status });
  }
  if (filters.date_from) chips.push({ k: 'إدخال من', v: filters.date_from });
  if (filters.date_to) chips.push({ k: 'إدخال إلى', v: filters.date_to });
  if (filters.approval_date_from) chips.push({ k: 'اعتماد من', v: filters.approval_date_from });
  if (filters.approval_date_to) chips.push({ k: 'اعتماد إلى', v: filters.approval_date_to });
  if (!chips.length) return null;
  return (
    <div className={`flex flex-wrap gap-2 text-xs ${className}`}>
      {chips.map((c, i) => (
        <span key={i} className="bg-white border border-orange-300 text-orange-800 px-2 py-1 rounded-full">
          <strong>{c.k}:</strong> {c.v}
        </span>
      ))}
    </div>
  );
}

// ===================================================================
// تبويب اللوحة التحليلية
function AnalyticsTab({ data }) {
  const k = data?.kpis || {};
  const pieData = [
    { name: 'معتمد', value: k.approved || 0, color: STATUS_COLORS.approved },
    { name: 'معلق', value: k.pending || 0, color: STATUS_COLORS.pending },
    { name: 'مرفوض', value: k.rejected || 0, color: STATUS_COLORS.rejected },
    { name: 'مسودة', value: k.draft || 0, color: STATUS_COLORS.draft },
  ].filter((p) => p.value > 0);
  
  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi label="إجمالي الكشوف" value={k.total_deliveries || 0} icon={Package} color="gray" />
        <Kpi label="إجمالي الاستلامات" value={k.total_receipts || 0} icon={Package} color="blue" />
        <Kpi label="معتمد" value={k.approved || 0} icon={CheckCircle2} color="green" />
        <Kpi label="معلق" value={k.pending || 0} icon={Clock} color="yellow" />
        <Kpi label="مرفوض" value={k.rejected || 0} icon={AlertTriangle} color="red" />
        <Kpi label="مسودة" value={k.draft || 0} icon={Package} color="gray" />
        <Kpi label="نسبة الاعتماد" value={k.approval_rate || 0} suffix="%" icon={TrendingUp} color="purple" />
        <Kpi label="متوسط الاعتماد" value={k.avg_approval_days || 0} suffix="يوم" icon={Clock} color="indigo" />
      </div>
      
      {/* الرسوم */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* سلسلة زمنية */}
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-gray-700"><Calendar className="w-4 h-4" /> الإدخالات بمرور الوقت</h3>
          {data?.timeline?.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="deliveries" stroke="#ea580c" name="كشوف" strokeWidth={2} />
                <Line type="monotone" dataKey="receipts" stroke="#2563eb" name="استلامات" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-gray-400 py-12 text-sm">لا توجد بيانات</p>}
        </div>
        
        {/* توزيع الحالات */}
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-gray-700"><BarChart3 className="w-4 h-4" /> توزيع الحالات</h3>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={(e) => `${e.name}: ${e.value}`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-gray-400 py-12 text-sm">لا توجد بيانات</p>}
        </div>
      </div>
      
      {/* أعلى 5 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TopList title="أعلى 5 موردين" icon={Building2} items={data?.top_suppliers} labelKey="supplier_name" valueKey="receipts" color="purple" />
        <TopList title="أعلى 5 مشاريع" icon={FolderKanban} items={data?.top_projects} labelKey="project_name" subKey="project_number" valueKey="receipts" color="blue" />
        <TopList title="أعلى 5 مدخلين" icon={UserCheck} items={data?.top_creators} labelKey="creator_name" valueKey="receipts" color="green" />
      </div>
    </div>
  );
}

function TopList({ title, icon: Icon, items = [], labelKey, subKey, valueKey, color }) {
  const ranks = ['bg-yellow-400', 'bg-gray-300', 'bg-orange-400', 'bg-blue-300', 'bg-purple-300'];
  return (
    <div className="bg-white border rounded-xl p-4">
      <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-gray-700"><Icon className="w-4 h-4" /> {title}</h3>
      {items?.length ? (
        <ol className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className={`w-6 h-6 ${ranks[i] || 'bg-gray-200'} text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0`}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{it[labelKey] || '—'}</div>
                {subKey && it[subKey] && <div className="text-xs text-gray-500">{it[subKey]}</div>}
              </div>
              <span className={`text-${color}-700 font-bold`}>{it[valueKey]}</span>
            </li>
          ))}
        </ol>
      ) : <p className="text-center text-gray-400 py-6 text-sm">لا توجد بيانات</p>}
    </div>
  );
}

// ===================================================================
// تبويب جدولي (للأنواع الأربعة)
function TableTab({ tab, data }) {
  const items = data?.report || data?.deliveries || [];
  if (!items.length) {
    return <div className="text-center py-12 text-gray-400">لا توجد بيانات مطابقة للفلاتر</div>;
  }
  
  // ===== قائمة الكشوف: تاريخ الإدخال وتاريخ الاعتماد النهائي =====
  if (tab === 'list') {
    const fmtDate = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');
    const statusBadge = (s) => {
      const map = {
        approved: 'bg-green-100 text-green-700',
        pending: 'bg-yellow-100 text-yellow-700',
        rejected: 'bg-red-100 text-red-700',
        draft: 'bg-gray-100 text-gray-700',
      };
      const labels = { approved: 'معتمد', pending: 'معلق', rejected: 'مرفوض', draft: 'مسودة' };
      return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[s] || 'bg-gray-100 text-gray-700'}`}>{labels[s] || s}</span>;
    };
    return (
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-orange-50 text-orange-900">
            <tr>
              <th className="text-right px-3 py-2">كشف #</th>
              <th className="text-right px-3 py-2">تاريخ التسليم</th>
              <th className="text-right px-3 py-2">الحالة</th>
              <th className="text-right px-3 py-2">موردين</th>
              <th className="text-right px-3 py-2">استلامات</th>
              <th className="text-right px-3 py-2">المدخل</th>
              <th className="text-right px-3 py-2 bg-orange-100">تاريخ الإدخال</th>
              <th className="text-right px-3 py-2">المعتمِد</th>
              <th className="text-right px-3 py-2 bg-indigo-100">تاريخ الاعتماد النهائي</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 font-bold">#{d.delivery_number}</td>
                <td className="px-3 py-2">{d.delivery_date}</td>
                <td className="px-3 py-2">{statusBadge(d.status)}</td>
                <td className="px-3 py-2 text-gray-700">{d.suppliers_count}</td>
                <td className="px-3 py-2 text-blue-700 font-bold">{d.receipts_count}</td>
                <td className="px-3 py-2 text-purple-700">{d.created_by || '—'}</td>
                <td className="px-3 py-2 bg-orange-50/50 font-medium">{fmtDate(d.created_at)}</td>
                <td className="px-3 py-2 text-indigo-700 font-medium">{d.approved_by || '—'}</td>
                <td className="px-3 py-2 bg-indigo-50/50 font-medium">{fmtDate(d.approved_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  
  if (tab === 'approver') {
    return (
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-indigo-50">
            <tr>
              <th className="text-right px-3 py-2">#</th>
              <th className="text-right px-3 py-2">المشرف (المعتمِد)</th>
              <th className="text-right px-3 py-2">كشوف معتمدة</th>
              <th className="text-right px-3 py-2">استلامات معتمدة</th>
              <th className="text-right px-3 py-2">متوسط وقت الاعتماد (يوم)</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                <td className="px-3 py-2 font-semibold">{it.approver_name || '—'}</td>
                <td className="px-3 py-2 text-indigo-700 font-bold">{it.total_approved_deliveries}</td>
                <td className="px-3 py-2 text-blue-700 font-bold">{it.total_approved_receipts}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${it.avg_approval_days <= 1 ? 'bg-green-100 text-green-700' : it.avg_approval_days <= 3 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{it.avg_approval_days ?? 0}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  
  if (tab === 'creator') {
    return (
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-green-50">
            <tr>
              <th className="text-right px-3 py-2">#</th>
              <th className="text-right px-3 py-2">المدخل</th>
              <th className="text-right px-3 py-2">كشوف</th>
              <th className="text-right px-3 py-2">استلامات</th>
              <th className="text-right px-3 py-2">معتمد</th>
              <th className="text-right px-3 py-2">معلق</th>
              <th className="text-right px-3 py-2">مرفوض</th>
              <th className="text-right px-3 py-2">مسودة</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                <td className="px-3 py-2 font-semibold">{it.creator_name || '—'}</td>
                <td className="px-3 py-2 text-gray-800 font-bold">{it.total_deliveries}</td>
                <td className="px-3 py-2 text-blue-700 font-bold">{it.total_receipts}</td>
                <td className="px-3 py-2 text-green-700">{it.approved_count}</td>
                <td className="px-3 py-2 text-yellow-700">{it.pending_count}</td>
                <td className="px-3 py-2 text-red-700">{it.rejected_count}</td>
                <td className="px-3 py-2 text-gray-500">{it.draft_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  
  // supplier / project — same shape
  const isSupplier = tab === 'supplier';
  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-3">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={items.slice(0, 10)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey={isSupplier ? 'supplier_name' : 'project_number'} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="approved_count" stackId="a" fill={STATUS_COLORS.approved} name="معتمد" />
            <Bar dataKey="pending_count" stackId="a" fill={STATUS_COLORS.pending} name="معلق" />
            <Bar dataKey="rejected_count" stackId="a" fill={STATUS_COLORS.rejected} name="مرفوض" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className={isSupplier ? 'bg-purple-50' : 'bg-blue-50'}>
            <tr>
              <th className="text-right px-3 py-2">#</th>
              <th className="text-right px-3 py-2">{isSupplier ? 'المورد' : 'المشروع'}</th>
              {!isSupplier && <th className="text-right px-3 py-2">اسم المشروع</th>}
              {isSupplier && <th className="text-right px-3 py-2">كشوف</th>}
              <th className="text-right px-3 py-2">استلامات</th>
              <th className="text-right px-3 py-2">معتمد</th>
              <th className="text-right px-3 py-2">معلق</th>
              <th className="text-right px-3 py-2">مرفوض</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                <td className="px-3 py-2 font-semibold">{isSupplier ? it.supplier_name : it.project_number}</td>
                {!isSupplier && <td className="px-3 py-2 text-gray-600">{it.project_name}</td>}
                {isSupplier && <td className="px-3 py-2 text-gray-800 font-bold">{it.total_deliveries}</td>}
                <td className="px-3 py-2 text-blue-700 font-bold">{it.total_receipts}</td>
                <td className="px-3 py-2 text-green-700">{it.approved_count}</td>
                <td className="px-3 py-2 text-yellow-700">{it.pending_count}</td>
                <td className="px-3 py-2 text-red-700">{it.rejected_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================================================================
// التصدير إلى Excel (CSV بترميز UTF-8 BOM)
function exportTabToExcel(tab, data, filters) {
  let rows = [];
  let headers = [];
  if (tab === 'list') {
    const fmtDate = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '');
    const statusMap = { approved: 'معتمد', pending: 'معلق', rejected: 'مرفوض', draft: 'مسودة' };
    headers = ['كشف #', 'تاريخ التسليم', 'الحالة', 'موردين', 'استلامات', 'المدخل', 'تاريخ الإدخال', 'المعتمِد', 'تاريخ الاعتماد النهائي'];
    rows = (data?.deliveries || []).map((d) => [
      d.delivery_number, d.delivery_date, statusMap[d.status] || d.status,
      d.suppliers_count, d.receipts_count,
      d.created_by, fmtDate(d.created_at),
      d.approved_by || '', fmtDate(d.approved_at),
    ]);
  } else if (tab === 'supplier') {
    headers = ['#', 'المورد', 'كشوف', 'استلامات', 'معتمد', 'معلق', 'مرفوض'];
    rows = (data?.report || []).map((it, i) => [i + 1, it.supplier_name, it.total_deliveries, it.total_receipts, it.approved_count, it.pending_count, it.rejected_count]);
  } else if (tab === 'project') {
    headers = ['#', 'رقم المشروع', 'اسم المشروع', 'استلامات', 'معتمد', 'معلق', 'مرفوض'];
    rows = (data?.report || []).map((it, i) => [i + 1, it.project_number, it.project_name, it.total_receipts, it.approved_count, it.pending_count, it.rejected_count]);
  } else if (tab === 'creator') {
    headers = ['#', 'المدخل', 'كشوف', 'استلامات', 'معتمد', 'معلق', 'مرفوض', 'مسودة'];
    rows = (data?.report || []).map((it, i) => [i + 1, it.creator_name, it.total_deliveries, it.total_receipts, it.approved_count, it.pending_count, it.rejected_count, it.draft_count]);
  } else if (tab === 'approver') {
    headers = ['#', 'المشرف', 'كشوف معتمدة', 'استلامات معتمدة', 'متوسط وقت الاعتماد (يوم)'];
    rows = (data?.report || []).map((it, i) => [i + 1, it.approver_name, it.total_approved_deliveries, it.total_approved_receipts, it.avg_approval_days ?? 0]);
  } else if (tab === 'analytics') {
    headers = ['المؤشر', 'القيمة'];
    const k = data?.kpis || {};
    rows = [
      ['إجمالي الكشوف', k.total_deliveries],
      ['إجمالي الاستلامات', k.total_receipts],
      ['معتمد', k.approved],
      ['معلق', k.pending],
      ['مرفوض', k.rejected],
      ['نسبة الاعتماد %', k.approval_rate],
      ['متوسط الاعتماد (يوم)', k.avg_approval_days],
    ];
  }
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const today = new Date().toISOString().slice(0, 10);
  a.download = `تقرير-${tab}-${today}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ===================================================================
// المكوّن الرئيسي
export function ReceiptReportsView({ token }) {
  const [activeTab, setActiveTab] = useState('analytics');
  const [filters, setFilters] = useState({
    supplier_name: '', project_number: '', creator_id: '', approver_id: '',
    status: '', date_from: '', date_to: '', approval_date_from: '', approval_date_to: '',
    receipt_number: ''
  });
  const [options, setOptions] = useState({ suppliers: [], projects: [], creators: [], approvers: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printMode, setPrintMode] = useState(false);

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  const clearFilters = () => setFilters({
    supplier_name: '', project_number: '', creator_id: '', approver_id: '',
    status: '', date_from: '', date_to: '', approval_date_from: '', approval_date_to: '',
    receipt_number: ''
  });

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p.toString();
  }, [filters]);

  // جلب خيارات الفلاتر مرة واحدة
  useEffect(() => {
    fetch(`${API_URL}/api/receipts/reports/filters-options`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : null).then(d => d && setOptions(d));
  }, [token]);

  // جلب بيانات التبويب الحالي
  const fetchTabData = useCallback(async () => {
    setLoading(true);
    try {
      const ep = activeTab === 'analytics' ? 'analytics'
        : activeTab === 'list' ? 'deliveries-list'
        : activeTab === 'supplier' ? 'by-supplier'
        : activeTab === 'project' ? 'by-project'
        : activeTab === 'creator' ? 'by-creator'
        : 'by-approver';
      const res = await fetch(`${API_URL}/api/receipts/reports/${ep}?${queryString}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [activeTab, queryString, token]);

  useEffect(() => { fetchTabData(); }, [fetchTabData]);

  const activeTabMeta = TABS.find(t => t.id === activeTab);

  if (printMode) {
    return <ReceiptReportsPrintView token={token} activeTab={activeTab} filters={filters} options={options} data={data} onClose={() => setPrintMode(false)} />;
  }

  return (
    <div>
      {/* شريط التبويبات */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="reports-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`reports-tab-${tab.id}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
                active ? 'bg-orange-600 text-white shadow' : 'bg-white border text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
      
      {/* لوحة الفلترة */}
      <FiltersPanel filters={filters} setFilters={setFilters} options={options} onClear={clearFilters} activeCount={activeFiltersCount} />
      
      {/* شريط الإجراءات */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <activeTabMeta.icon className="w-4 h-4" />
          <span className="font-medium">{activeTabMeta.label}</span>
          {activeFiltersCount > 0 && (
            <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs" data-testid="active-filters-badge">{activeFiltersCount} فلتر</span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => exportTabToExcel(activeTab, data, filters)}
            disabled={loading || !data}
            data-testid="export-excel-btn"
            className="h-10 px-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button
            onClick={() => setPrintMode(true)}
            disabled={loading || !data}
            data-testid="print-report-btn"
            className="h-10 px-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> طباعة / PDF
          </button>
        </div>
      </div>
      
      {/* محتوى التبويب */}
      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-600 mx-auto" /></div>
      ) : activeTab === 'analytics' ? (
        <AnalyticsTab data={data} />
      ) : (
        <TableTab tab={activeTab} data={data} />
      )}
    </div>
  );
}

// ===================================================================
// نافذة الطباعة (Portal)
function ReceiptReportsPrintView({ activeTab, filters, options, data, onClose }) {
  const activeMeta = TABS.find(t => t.id === activeTab);
  const handlePrint = () => window.print();
  
  return (
    <div className="fixed inset-0 z-50 bg-white overflow-auto" dir="rtl" data-print-root>
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          html, body { height: auto !important; overflow: visible !important; }
          body > *:not([data-print-root]) { display: none !important; }
          [data-print-root] { position: static !important; width: 100% !important; height: auto !important; }
          .print-hide { display: none !important; }
          .recharts-wrapper, .recharts-surface { overflow: visible !important; }
        }
      `}</style>
      <div className="print-hide bg-gray-50 border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-gray-200 flex items-center justify-center" data-testid="close-print-view">
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 font-bold">معاينة طباعة: {activeMeta?.label}</h2>
        <button onClick={handlePrint} data-testid="print-action-btn" className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 font-semibold">
          <Printer className="w-4 h-4" /> طباعة / حفظ PDF
        </button>
      </div>
      <div className="max-w-6xl mx-auto p-6 print:p-2">
        <div className="text-center mb-4">
          <h1 className="text-2xl print:text-xl font-bold">تقرير كشوف الاستلام — {activeMeta?.label}</h1>
          <p className="text-sm text-gray-600 mt-1">تاريخ الطباعة: {new Date().toLocaleDateString('en-US')}</p>
        </div>
        
        <div className="border border-orange-200 bg-orange-50 rounded-lg p-3 mb-4">
          <div className="text-xs font-bold text-orange-900 mb-2">الفلاتر المُطبَّقة:</div>
          <ActiveFilterChips filters={filters} options={options} />
          {!Object.values(filters).filter(Boolean).length && <span className="text-xs text-gray-500">لم يتم تطبيق أي فلتر — التقرير شامل لكامل البيانات</span>}
        </div>
        
        {activeTab === 'analytics' ? <AnalyticsTab data={data} /> : <TableTab tab={activeTab} data={data} />}
      </div>
    </div>
  );
}
